// Field-level validation rules.

/** EIN format: XX-XXXXXXX with hyphen, or XXXXXXXXX without hyphen */
export function isValidEin(value: string): boolean {
  return /^\d{2}-\d{7}$/.test(value);// || /^\d{9}$/.test(value);
}

/**
 * Formats EIN input as user types.
 * - Removes all non-numeric characters
 * - Inserts hyphen after 2 digits (XX-XXXXXXX format)
 * - Limits to 9 digits total
 * @param value Raw input value from user
 * @returns Formatted EIN string in XX-XXXXXXX format or partial format while typing
 */
export function formatEIN(value: string): string {
  // Remove all non-numeric characters
  const digitsOnly = value.replace(/\D/g, '');

  // Limit to 9 digits max
  const limited = digitsOnly.slice(0, 9);

  // Format as XX-XXXXXXX (insert hyphen after 2 digits)
  if (limited.length <= 2) {
    return limited;
  }
  return `${limited.slice(0, 2)}-${limited.slice(2)}`;
}

/** Last 4 digits of SSN: exactly 4 digits */
export function isValidSsn4(value: string): boolean {
  return /^\d{4}$/.test(value);
}

/** Full SSN: exactly 9 digits (no dashes) */
export function isValidSsnFull(value: string): boolean {
  return /^\d{9}$/.test(value);
}

/** US ZIP code: 5 digits or 5+4 format */
export function isValidZip(value: string): boolean {
  return /^\d{5}(-\d{4})?$/.test(value);
}

/** Non-empty trimmed string */
export function isNonEmpty(value: string): boolean {
  return value.trim().length > 0;
}

/** Basic email format check */
export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,63}$/.test(value);

}

// ── Identity Verification validators (Step 2.2) ─────────────────────────────

export function validateEIN(value: string): string | null {
  if (!value.trim())      return 'EIN is required.';
  if (!isValidEin(value)) return 'EIN must be in the format XX-XXXXXXX (e.g., 12-3456789).';
  return null;
}

export function validateSSN4(value: string): string | null {
  if (!value.trim())           return 'Last 4 digits of SSN are required.';
  if (!isValidSsn4(value))     return 'Enter exactly 4 numeric digits.';
  return null;
}

export function validateZIPCode(value: string): string | null {
  if (!value.trim())           return 'ZIP Code is required.';
  if (!/^\d{5}$/.test(value))  return 'ZIP Code must be exactly 5 digits.';
  return null;
}

export function validateCaptcha(value: string): string | null {
  if (!value.trim()) return 'Please enter the characters shown in the image.';
  return null;
}

// ── Form Access validators (Step 3) ──────────────────────────────────────────
// Client-side format validation (Stage 1) only. Server performs business logic validation.

/**
 * Validates first name format.
 * - Required
 * - 2-50 characters
 * - Allows: letters, spaces, hyphens, apostrophes, dots
 * Example: "Jean-Pierre", "Mary Jane", "O'Brien", "José"
 */
export function validateFirstNameFormat(value: string): string | null {
  if (!value.trim())
    return 'First name is required.';
  if (value.trim().length < 1)
    return 'First name must be at least 1 character.';
  if (value.length > 60)
    return 'First name must not exceed 60 characters.';
  if (!/^[a-zA-Z\s\-'\.]+$/.test(value))
    return 'Names may contain only letters, spaces, hyphens (-), apostrophes (\'), and periods (.). Accented characters (such as é, ñ, or ü) are not supported.';
  return null;
}

/**
 * Validates last name format.
 * - Required (mandatory field)
 * - 2-50 characters
 * - Allows: letters, spaces, hyphens, apostrophes, dots
 * Example: "Smith", "O'Connor", "van der Berg"
 */
export function validateLastNameFormat(value: string): string | null {
  if (!value.trim())
    return 'Last name is required.';
  if (value.trim().length < 1)
    return 'Last name must be at least 1 characters.';
  if (value.length > 60)
    return 'Last name must not exceed 60 characters.';
  if (!/^[a-zA-Z\s\-'\.]+$/.test(value))
    return 'Names may contain only letters, spaces, hyphens (-), apostrophes (\'), and periods (.). Accented characters (such as é, ñ, or ü) are not supported.';
  return null;
}

/**
 * Validates SSN4 format for Step 3 form access.
 * - Required
 * - Exactly 4 numeric digits
 */
export function validateSSN4Format(value: string): string | null {
  if (!value.trim())
    return 'Last 4 digits of SSN are required.';
  if (!/^\d{4}$/.test(value))
    return 'Enter exactly 4 numeric digits.';
  return null;
}

/**
 * Validates email format for Step 3 form access.
 * - Optional
 * - If provided: valid email format, max 254 characters
 */
export function validateEmailFormat(value: string): string | null {
  if (!value.trim())
    return null;  // optional field
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,63}$/;

  if (!emailRegex.test(value))
    return 'Please enter a valid email address.';
  if (value.length > 254)
    return 'Email address must not exceed 254 characters.';
  return null;
}

/**
 * Validates email re-entry matches the original email.
 * - Required if email is provided
 * - Must match original email exactly (case-insensitive)
 */
export function validateEmailMatch(email: string, emailVerify: string): string | null {
  // If no email provided, skip verification
  if (!email.trim()) {
    if (!emailVerify.trim())
      return null;  // both empty, no error
    return 'Please provide the original email address first.';
  }

  // Email is provided, so verification is required
  if (!emailVerify.trim())
    return 'Please confirm your email address.';

  // Check if emails match (case-insensitive)
  if (email.trim().toLowerCase() !== emailVerify.trim().toLowerCase())
    return 'Email addresses do not match. Please check and try again.';

  return null;
}
